import { useEffect, useState } from "react";
import { Link, useSearchParams } from "react-router-dom";
import { toast } from "sonner";
import { Check, Gift } from "lucide-react";
import { api, errMsg } from "../lib/api";
import { useAuth } from "../lib/auth";
import { PATHS, countries, useLang, useT } from "../lib/i18n";
import { track } from "../lib/tracking";
import { Button } from "../components/ui/button";
import { Switch } from "../components/ui/switch";
import { Faq, RecordStats } from "../components/site/Blocks";
import { Wrap } from "./PublicPage";
import { FreeVipHero, FreeVipSteps, FreeVipBenefits, FreeVipFaq, FreeVipNote } from "../components/site/FreeVipPitch";

export function BookieList({ country, setCountry, compact }) {
  const lang = useLang();
  const t = useT();
  const { config } = useAuth();
  const [list, setList] = useState(null);
  const [all, setAll] = useState(false);
  const top = config?.top_bookies || 3;
  useEffect(() => {
    setAll(false);
    if (!country) { setList(null); return; }
    api.get("/bookies", { params: { country } }).then((r) => setList(r.data));
  }, [country]);
  const goHref = (b) => `/go/${b.slug || b.id}?src=web${country ? `&c=${country}` : ""}`;
  const click = (b) => track("bookie_click", { bookie: b.name, country });
  return (
    <div data-testid="bookie-list">
      <select value={country} onChange={(e) => setCountry(e.target.value)} className="h-10 w-full rounded-lg border border-input bg-secondary px-3 text-sm" data-testid="bookie-country-select">
        <option value="">{t("Land wählen", "Select country")}</option>
        {countries(lang).map(([c, n]) => <option key={c} value={c}>{n}</option>)}
      </select>
      {list && list.length > 0 && (
        <div className="mt-4 grid gap-3 sm:grid-cols-2">
          {(all ? list : list.slice(0, top)).map((b) => (
            <a key={b.id} href={goHref(b)} target="_blank" rel="nofollow sponsored noopener" onClick={() => click(b)} className="surface surface-hover flex items-center gap-3 p-4 text-left" data-testid={`bookie-${b.id}`}>
              {b.logo_url ? <img src={b.logo_url} alt={b.name} className="h-10 w-20 object-contain" /> : <span className="font-display text-xl font-bold">{b.name}</span>}
              <span className="ml-auto text-sm text-[var(--ball)]">{t("Registrieren", "Sign up")} →</span>
            </a>
          ))}
          {list.length > top && (
            <button type="button" onClick={() => setAll(!all)} className="rounded-xl border border-dashed border-border p-4 text-left text-sm text-muted-foreground transition-colors hover:border-[var(--ball)] hover:text-foreground sm:col-span-2" data-testid="bookie-more-toggle">
              {all ? t("Weniger anzeigen", "Show less") : `${t("Bei allen schon registriert? Weitere Anbieter", "Already registered everywhere? More bookies")} (${list.length - top})`}
            </button>
          )}
        </div>
      )}
      {list && list.length === 0 && (
        <div className="mt-4 rounded-xl border border-border bg-secondary/50 p-4 text-sm" data-testid="no-bookie-offer">
          <p>{t("Für dein Land gibt es aktuell keinen Partner-Buchmacher. Als Ausgleich:", "There is no partner bookmaker for your country yet. Instead:")}</p>
          <p className="mt-2 font-semibold text-[var(--ball)]">{t("50% auf den ersten Monat oder 30% aufs Jahrespaket", "50% off the first month or 30% off the annual package")}</p>
          {!compact && <Link to={`${PATHS[lang].vip}?offer=nobookie`}><Button size="sm" className="mt-3 rounded-full" data-testid="no-bookie-offer-btn">{t("Angebot nutzen", "Use offer")}</Button></Link>}
        </div>
      )}
      {!compact && <p className="mt-3 text-xs text-muted-foreground">{t(`Bedingung: Registrierung über unseren Link + Einzahlung von mind. ${config?.bookie_min_deposit || 100} €. Danach Screenshot im Konto oder per Telegram hochladen.`, `Condition: sign up via our link + deposit at least €${config?.bookie_min_deposit || 100}. Then upload a screenshot in your account or via Telegram.`)}</p>}
    </div>
  );
}

function Product({ id, title, price, priceSms, desc, note, sms, setSms, onBuy, busy, highlight }) {
  const t = useT();
  return (
    <div className={`surface relative flex flex-col p-6 ${highlight ? "border-[var(--ball)]/60 shadow-[0_0_60px_-20px_rgba(215,255,58,.4)]" : ""}`} data-testid={`product-${id}`}>
      {highlight && <span className="absolute -top-3 left-6 rounded-full bg-primary px-3 py-1 text-xs font-bold text-primary-foreground">{t("Bester Wert", "Best value")}</span>}
      <h3 className="font-display text-2xl font-bold">{title}</h3>
      <p className="mt-1 text-sm text-muted-foreground">{desc}</p>
      <p className="mt-6 font-num text-5xl font-bold">{sms ? priceSms : price}€<span className="text-sm font-normal text-muted-foreground">{note}</span></p>
      <label className="mt-5 flex items-center justify-between rounded-lg bg-secondary/60 px-3 py-2 text-sm">
        <span>{t("SMS-Option", "SMS option")} <span className="text-muted-foreground">(+{priceSms - price}€)</span></span>
        <Switch checked={sms} onCheckedChange={setSms} data-testid={`product-${id}-sms`} />
      </label>
      <Button className="mt-5 rounded-full font-semibold" disabled={busy} onClick={onBuy} data-testid={`product-${id}-buy`}>{t("Jetzt kaufen", "Buy now")}</Button>
    </div>
  );
}

export default function VipPage({ p }) {
  const lang = useLang();
  const t = useT();
  const { user } = useAuth();
  const [sp] = useSearchParams();
  const d = p.data;
  const pr = d.products;
  const tg = sp.get("tg");
  const [sms, setSms] = useState({ monthly: false, grand_slam: false, annual: false, [sp.get("product") || "x"]: sp.get("sms") === "1" });
  const [busy, setBusy] = useState(false);
  const [country, setCountry] = useState(user?.country || "");
  const buy = async (product) => {
    if (!user && !tg) { window.location.href = `${PATHS[lang].register}?next=vip`; return; }
    setBusy(true);
    const value = sms[product] ? pr[product].price_sms : pr[product].price;
    track("begin_checkout", { value, currency: "EUR", product, items: [{ item_id: product, price: value }] });
    try {
      const { data } = await api.post("/checkout", { product, sms: sms[product], offer: sp.get("offer"), tg });
      window.location.href = data.url;
    } catch (e) { toast.error(errMsg(e)); setBusy(false); }
  };
  const S = (k) => ({ sms: sms[k], setSms: (v) => setSms({ ...sms, [k]: v }), busy, onBuy: () => buy(k) });
  return (
    <Wrap>
      <div className="max-w-3xl">
        <p className="text-xs font-semibold uppercase tracking-[0.22em] text-[var(--ball)]">VIP</p>
        <h1 className="mt-2 font-display text-5xl font-extrabold sm:text-6xl" data-testid="page-h1">{d.page.h1}</h1>
        <p className="mt-3 text-muted-foreground">{t("Alle Tipps per Website, E-Mail und Telegram. Mit SMS-Option bekommst du zusätzlich eine SMS, sobald neue Tipps online sind.", "All tips via website, email and Telegram. With the SMS option you also get an SMS as soon as new tips are online.")}</p>
        {tg && <p className="mt-4 rounded-lg border border-[var(--ball)]/40 bg-[var(--ball)]/5 p-3 text-sm" data-testid="tg-checkout-note">{t("Kauf über Telegram – wird deinem verknüpften Konto gutgeschrieben.", "Purchase via Telegram – credited to your linked account.")}</p>}
        {sp.get("offer") === "nobookie" && <p className="mt-4 rounded-lg border border-[var(--ball)]/40 bg-[var(--ball)]/5 p-3 text-sm">{t("Dein Rabatt (50% Monat / 30% Jahr) wird im Checkout angewendet, falls für dein Land kein Partner-Buchmacher existiert.", "Your discount (50% month / 30% annual) is applied at checkout if no partner bookmaker exists for your country.")}</p>}
      </div>
      {d.record && <div className="mt-10"><RecordStats total={d.record} /></div>}
      <div className="mt-10 grid gap-5 lg:grid-cols-3">
        <Product id="monthly" title={t("Monatlich", "Monthly")} price={pr.monthly.price} priceSms={pr.monthly.price_sms} note={t("/Monat", "/month")} desc={t("Alle Tipps außer Grand Slams: ATP, WTA, Challenger, WTA 125, ITF. Monatlich kündbar.", "All tips except Grand Slams: ATP, WTA, Challenger, WTA 125, ITF. Cancel monthly.")} {...S("monthly")} />
        {d.gs_on_sale ? <Product id="grand_slam" title={`Grand Slam Pass: ${d.gs_on_sale.name}`} price={pr.grand_slam.price} priceSms={pr.grand_slam.price_sms} note={t(" einmalig", " one-time")} desc={t("Alle Tipps zum Grand Slam inkl. Qualifikation.", "All tips for this Grand Slam incl. qualifying.")} {...S("grand_slam")} />
          : <div className="surface flex items-center p-6 text-sm text-muted-foreground" data-testid="product-grand_slam-unavailable">{t("Der nächste Grand Slam Pass ist bald erhältlich.", "The next Grand Slam pass will be available soon.")}</div>}
        <Product id="annual" highlight title={t("Jahrespaket", "Annual package")} price={pr.annual.price} priceSms={pr.annual.price_sms} note={t(" einmalig", " one-time")} desc={t(`Alle Tipps inkl. aller Grand Slams – gültig bis ${d.annual_end.split("-").reverse().join(".")}.`, `All tips incl. all Grand Slams – valid until ${d.annual_end}.`)} {...S("annual")} />
      </div>
      <section className="mt-16 space-y-6" data-testid="free-vip-section">
        <FreeVipHero><FreeVipSteps /></FreeVipHero>
        <div className="grid gap-8 lg:grid-cols-2">
          <BookieList country={country} setCountry={setCountry} />
          <div className="space-y-6"><FreeVipBenefits /><FreeVipFaq /></div>
        </div>
        <FreeVipNote />
      </section>
      <Faq items={p.faq} />
    </Wrap>
  );
}
